import Ionicons from "@expo/vector-icons/Ionicons";
import { useFocusEffect, useRouter } from "expo-router";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

import { spotApi } from "@/api";
import { Logo } from "@/components/brand/logo";
import { ThemedText } from "@/components/themed-text";
import { Radius, Spacing } from "@/constants/theme";
import { useTheme } from "@/hooks/use-theme";
import { useAuthStore } from "@/state/auth-store";
import type { OwnerSpot } from "@/types";
import i18n from "@/i18n";

const STATUS_COPY: Record<OwnerSpot["status"], string> = {
  get pending() {
    return i18n.t("business.home.status.pending");
  },
  get approved() {
    return i18n.t("business.home.status.approved");
  },
  get rejected() {
    return i18n.t("business.home.status.rejected");
  },
};

export default function BusinessHome() {
  useTranslation();
  const c = useTheme();
  const router = useRouter();
  const status = useAuthStore((s) => s.status);
  const isAuthed = status === "authenticated";

  const [spots, setSpots] = useState<OwnerSpot[]>([]);
  const [loading, setLoading] = useState(false);

  useFocusEffect(
    useCallback(() => {
      if (!isAuthed) return;
      setLoading(true);
      spotApi
        .mySpots()
        .then(setSpots)
        .catch(() => setSpots([]))
        .finally(() => setLoading(false));
    }, [isAuthed]),
  );

  return (
    <SafeAreaView style={[styles.safe, { backgroundColor: c.background }]}>
      <ScrollView contentContainerStyle={styles.content}>
        <Logo size={26} />

        <View style={styles.hero}>
          <View
            style={[styles.pill, { backgroundColor: c.backgroundSelected }]}
          >
            <ThemedText variant="caption" tone="tint">
              {i18n.t("business.home.spots")}
            </ThemedText>
          </View>
          <ThemedText variant="display">
            {i18n.t("business.home.headline")}
          </ThemedText>
          <ThemedText variant="body" tone="secondary">
            {i18n.t("business.home.intro")}
          </ThemedText>

          <Pressable
            accessibilityRole="button"
            onPress={() => router.push("/business/claim")}
            style={[styles.cta, { backgroundColor: c.tint }]}
          >
            <Ionicons name="storefront-outline" size={18} color={c.onTint} />
            <ThemedText variant="bodyStrong" tone="onTint">
              {i18n.t("business.home.claim")}
            </ThemedText>
          </Pressable>
        </View>

        {isAuthed ? (
          <View style={styles.section}>
            <ThemedText
              variant="label"
              tone="muted"
              style={styles.sectionLabel}
            >
              {i18n
                .t("business.home.yourSpots")
                .toLocaleUpperCase(i18n.resolvedLanguage)}
            </ThemedText>

            {loading ? (
              <ActivityIndicator color={c.tint} style={styles.loader} />
            ) : spots.length === 0 ? (
              <View
                style={[
                  styles.empty,
                  { backgroundColor: c.surfaceAlt, borderColor: c.border },
                ]}
              >
                <ThemedText variant="body" tone="secondary" center>
                  {i18n.t("business.home.empty")}
                </ThemedText>
              </View>
            ) : (
              spots.map((spot) => (
                <Pressable
                  key={spot.id}
                  onPress={() => router.push(`/business/spots/${spot.id}`)}
                  style={[
                    styles.spotRow,
                    { backgroundColor: c.surface, borderColor: c.border },
                  ]}
                >
                  <View style={styles.spotCopy}>
                    <ThemedText variant="bodyStrong">
                      {spot.venueName}
                    </ThemedText>
                    <ThemedText variant="caption" tone="secondary">
                      {STATUS_COPY[spot.status]}
                    </ThemedText>
                  </View>
                  <Ionicons
                    name="chevron-forward"
                    size={20}
                    color={c.textMuted}
                  />
                </Pressable>
              ))
            )}
          </View>
        ) : (
          <View
            style={[
              styles.empty,
              { backgroundColor: c.surfaceAlt, borderColor: c.border },
            ]}
          >
            <ThemedText variant="body" tone="secondary" center>
              {i18n.t("business.home.signInBody")}
            </ThemedText>
            <Pressable
              onPress={() => router.push("/(auth)/welcome")}
              style={[styles.secondaryBtn, { borderColor: c.border }]}
            >
              <ThemedText variant="bodyStrong" tone="tint">
                {i18n.t("business.home.signIn")}
              </ThemedText>
            </Pressable>
          </View>
        )}
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1 },
  content: {
    width: "100%",
    maxWidth: 640,
    alignSelf: "center",
    padding: Spacing.xl,
    gap: Spacing.xl,
  },
  hero: { gap: Spacing.md, alignItems: "flex-start" },
  pill: {
    paddingHorizontal: Spacing.md,
    paddingVertical: 6,
    borderRadius: Radius.pill,
  },
  cta: {
    marginTop: Spacing.sm,
    minHeight: 52,
    paddingHorizontal: Spacing.xl,
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.sm,
    borderRadius: Radius.lg,
  },
  section: { gap: Spacing.sm },
  sectionLabel: { letterSpacing: 1 },
  loader: { marginVertical: Spacing.lg },
  empty: {
    padding: Spacing.lg,
    gap: Spacing.md,
    borderRadius: Radius.lg,
    borderWidth: StyleSheet.hairlineWidth,
    alignItems: "center",
  },
  secondaryBtn: {
    minHeight: 44,
    paddingHorizontal: Spacing.lg,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: Radius.lg,
    borderWidth: 1,
  },
  spotRow: {
    minHeight: 64,
    paddingHorizontal: Spacing.lg,
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.md,
    borderRadius: Radius.lg,
    borderWidth: StyleSheet.hairlineWidth,
  },
  spotCopy: { flex: 1, gap: 2 },
});
